// src/lib/utils/debounce.ts
export type Debounced<TArgs extends unknown[]> =
  ((..._args: TArgs) => void) & {
    cancel: () => void;
    flush: () => void;
  };

export function debounce<TArgs extends unknown[]>(
  fn: (..._args: TArgs) => unknown,
  wait = 300
): Debounced<TArgs> {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let lastArgs: TArgs | null = null;

  const run = () => {
    timer = null;
    if (lastArgs) {
      fn(...lastArgs);
      lastArgs = null;
    }
  };

  const debounced = ((..._args: TArgs) => {
    lastArgs = _args;
    if (timer !== null) clearTimeout(timer);
    timer = setTimeout(run, wait);
  }) as Debounced<TArgs>;

  debounced.cancel = () => {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
    lastArgs = null;
  };

  debounced.flush = () => {
    if (timer !== null) {
      clearTimeout(timer);
      run();
    }
  };

  return debounced;
}
